import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Search,
  Target,
  TrendingUp,
  X,
  Star
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { value: "not_started", label: "Not Started" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed", label: "Completed" },
  { value: "on_hold", label: "On Hold" },
];

const CATEGORIES = [
  "Circulation",
  "Programs",
  "Outreach",
  "Collections",
  "Technology",
  "Staff Development",
  "Community Engagement",
  "Facilities",
  "Other"
];

const initialFormData = {
  title: "",
  description: "",
  category: "",
  target_value: 100,
  current_value: 0,
  unit: "%",
  year: new Date().getFullYear(),
  status: "not_started",
  milestones: [],
  notes: ""
};

export const YearlyGoals = ({ viewMode, userLookup = {} }) => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [milestoneInput, setMilestoneInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [yearFilter, setYearFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [isEditing, setIsEditing] = useState(false);

  // Check if showing all data based on viewMode
  const isShowingAllData = viewMode !== "staff";
  
  // Helper to get staff name from userLookup
  const getStaffName = (createdBy) => {
    if (!createdBy || !userLookup[createdBy]) return null;
    return userLookup[createdBy].name;
  };

  useEffect(() => {
    fetchGoals();
  }, []);

  const fetchGoals = async () => {
    try {
      const response = await axios.get(`${API}/yearly-goals`);
      setGoals(response.data);
    } catch (error) {
      toast.error("Failed to fetch yearly goals");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        target_value: parseInt(formData.target_value) || 100,
        current_value: parseInt(formData.current_value) || 0,
        year: parseInt(formData.year),
      };

      if (isEditing && selectedGoal) {
        await axios.put(`${API}/yearly-goals/${selectedGoal.id}`, submitData);
        toast.success("Goal updated successfully");
      } else {
        await axios.post(`${API}/yearly-goals`, submitData);
        toast.success("Goal created successfully");
      }
      setDialogOpen(false);
      resetForm();
      fetchGoals();
    } catch (error) {
      toast.error(isEditing ? "Failed to update goal" : "Failed to create goal");
    }
  };

  const handleDelete = async () => {
    if (!selectedGoal) return;
    try {
      await axios.delete(`${API}/yearly-goals/${selectedGoal.id}`);
      toast.success("Goal deleted successfully");
      setDeleteDialogOpen(false);
      setSelectedGoal(null);
      fetchGoals();
    } catch (error) {
      toast.error("Failed to delete goal");
    }
  };

  const handleEdit = (goal) => {
    setSelectedGoal(goal);
    setFormData({
      title: goal.title,
      description: goal.description,
      category: goal.category,
      target_value: goal.target_value,
      current_value: goal.current_value,
      unit: goal.unit,
      year: goal.year,
      status: goal.status,
      milestones: goal.milestones || [],
      notes: goal.notes || ""
    });
    setIsEditing(true);
    setDialogOpen(true);
  };

  const handleSetPrimary = async (goalId) => {
    try {
      await axios.post(`${API}/yearly-goals/${goalId}/set-primary`);
      toast.success("Primary goal updated!");
      fetchGoals();
    } catch (error) {
      toast.error("Failed to set primary goal");
    }
  };

  const updateProgress = async (goal, newValue) => {
    try {
      await axios.put(`${API}/yearly-goals/${goal.id}`, {
        current_value: newValue
      });
      toast.success("Progress updated");
      fetchGoals();
    } catch (error) {
      toast.error("Failed to update progress");
    }
  };

  const resetForm = () => {
    setFormData(initialFormData);
    setSelectedGoal(null);
    setIsEditing(false);
    setMilestoneInput("");
  };

  const addMilestone = () => {
    if (milestoneInput.trim()) {
      setFormData({
        ...formData,
        milestones: [...formData.milestones, milestoneInput.trim()]
      });
      setMilestoneInput("");
    }
  };

  const removeMilestone = (index) => {
    setFormData({
      ...formData,
      milestones: formData.milestones.filter((_, i) => i !== index)
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      not_started: "bg-stone-100 text-stone-600",
      in_progress: "bg-blue-100 text-blue-700",
      completed: "bg-emerald-100 text-emerald-700",
      on_hold: "bg-amber-100 text-amber-700"
    };
    return styles[status] || styles.not_started;
  };

  const years = [...new Set(goals.map(g => g.year))].sort((a, b) => b - a);

  const filteredGoals = goals.filter((goal) => {
    const matchesSearch = goal.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      goal.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesYear = yearFilter === "all" || goal.year.toString() === yearFilter;
    const matchesStatus = statusFilter === "all" || goal.status === statusFilter;
    return matchesSearch && matchesYear && matchesStatus;
  });

  // Calculate overall stats
  const totalGoals = filteredGoals.length;
  const completedGoals = filteredGoals.filter(g => g.status === "completed").length;
  const avgProgress = totalGoals > 0 
    ? Math.round(filteredGoals.reduce((acc, g) => acc + (g.current_value / g.target_value * 100), 0) / totalGoals)
    : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="goals-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="yearly-goals-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">
            Yearly Goals
          </h1>
          <p className="text-muted-foreground mt-1">
            Track and manage your library's annual objectives
          </p>
        </div>
        <Button
          onClick={() => {
            resetForm();
            setDialogOpen(true);
          }}
          data-testid="create-goal-btn"
          className="btn-animate"
        >
          <Plus className="mr-2 h-4 w-4" /> New Goal
        </Button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-stone-200" data-testid="total-goals-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Total Goals</p>
                <p className="text-2xl font-semibold mt-1">{totalGoals}</p>
              </div>
              <Target className="h-8 w-8 text-blue-600 opacity-80" strokeWidth={1.5} />
            </div>
          </CardContent>
        </Card>
        <Card className="border-stone-200" data-testid="completed-goals-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Completed</p>
                <p className="text-2xl font-semibold mt-1">{completedGoals}</p>
              </div>
              <Target className="h-8 w-8 text-emerald-600 opacity-80" strokeWidth={1.5} />
            </div>
          </CardContent>
        </Card>
        <Card className="border-stone-200" data-testid="avg-progress-stat">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Avg Progress</p>
                <p className="text-2xl font-semibold mt-1">{avgProgress}%</p>
              </div>
              <TrendingUp className="h-8 w-8 text-amber-600 opacity-80" strokeWidth={1.5} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search goals..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-goals"
          />
        </div>
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger className="w-full sm:w-36" data-testid="year-filter">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {years.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            {STATUSES.map((status) => (
              <SelectItem key={status.value} value={status.value}>
                {status.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Goals Grid */}
      {filteredGoals.length === 0 ? (
        <Card className="border-stone-200" data-testid="no-goals">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Target className="h-12 w-12 text-muted-foreground mb-4" strokeWidth={1.5} />
            <h3 className="font-medium text-lg">No goals found</h3>
            <p className="text-muted-foreground text-sm mt-1">
              {searchQuery || yearFilter !== "all" || statusFilter !== "all"
                ? "Try adjusting your filters"
                : "Create your first yearly goal"}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredGoals.map((goal, index) => {
            const progress = goal.target_value > 0 
              ? Math.round((goal.current_value / goal.target_value) * 100) 
              : 0;
            return (
              <Card
                key={goal.id}
                className={cn(
                  "card-hover border-stone-200 animate-slide-up",
                  goal.is_primary && "ring-2 ring-amber-400 border-amber-400"
                )}
                style={{ animationDelay: `${index * 0.05}s` }}
                data-testid={`goal-card-${goal.id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        {goal.is_primary && (
                          <Badge className="bg-amber-100 text-amber-700 border-amber-300">
                            <Star className="h-3 w-3 mr-1 fill-amber-500" />
                            Primary Goal
                          </Badge>
                        )}
                        <Badge variant="outline" className="text-xs font-mono">
                          {goal.year}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {goal.category}
                        </Badge>
                      </div>
                      <CardTitle className="font-heading text-lg line-clamp-2">
                        {goal.title}
                      </CardTitle>
                      {isShowingAllData && getStaffName(goal.created_by) && (
                        <p className="text-xs text-blue-600 mt-1">
                          Staff: {getStaffName(goal.created_by)}
                        </p>
                      )}
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`goal-menu-${goal.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!goal.is_primary && (
                          <DropdownMenuItem
                            onClick={() => handleSetPrimary(goal.id)}
                            data-testid={`set-primary-${goal.id}`}
                            className="text-amber-600"
                          >
                            <Star className="mr-2 h-4 w-4" /> Set as Primary
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem
                          onClick={() => handleEdit(goal)}
                          data-testid={`edit-goal-${goal.id}`}
                        >
                          <Pencil className="mr-2 h-4 w-4" /> Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => {
                            setSelectedGoal(goal);
                            setDeleteDialogOpen(true);
                          }}
                          data-testid={`delete-goal-${goal.id}`}
                        >
                          <Trash2 className="mr-2 h-4 w-4" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                    {goal.description}
                  </p>
                  
                  {/* Progress Section */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-mono font-medium">
                        {goal.current_value}/{goal.target_value} {goal.unit}
                      </span>
                    </div>
                    <Progress value={progress} className="h-2" />
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground">{progress}% complete</span>
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => updateProgress(goal, Math.max(0, goal.current_value - 10))}
                          disabled={goal.current_value <= 0}
                          data-testid={`decrease-progress-${goal.id}`}
                        >
                          -10
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => updateProgress(goal, Math.min(goal.target_value, goal.current_value + 10))}
                          disabled={goal.current_value >= goal.target_value}
                          data-testid={`increase-progress-${goal.id}`}
                        >
                          +10
                        </Button>
                      </div>
                    </div>
                  </div>

                  {/* Milestones */}
                  {goal.milestones && goal.milestones.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-stone-100">
                      <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Milestones</p>
                      <div className="space-y-1">
                        {goal.milestones.slice(0, 3).map((milestone, i) => (
                          <p key={i} className="text-xs text-muted-foreground">• {milestone}</p>
                        ))}
                        {goal.milestones.length > 3 && (
                          <p className="text-xs text-muted-foreground">+{goal.milestones.length - 3} more</p>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-stone-100">
                    <Badge className={getStatusBadge(goal.status)}>
                      {goal.status.replace("_", " ")}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">
              {isEditing ? "Edit Goal" : "Create New Goal"}
            </DialogTitle>
            <DialogDescription>
              {isEditing ? "Update the goal details" : "Set a new yearly objective for your library"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-4">
              <div className="form-section">
                <h3 className="font-medium mb-3">Goal Information</h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="title">Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Goal title"
                      required
                      data-testid="goal-title-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="description">Description *</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Describe the goal..."
                      rows={3}
                      required
                      data-testid="goal-description-input"
                    />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category">Category *</Label>
                      <Select
                        value={formData.category}
                        onValueChange={(value) => setFormData({ ...formData, category: value })}
                      >
                        <SelectTrigger data-testid="goal-category-select">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map((cat) => (
                            <SelectItem key={cat} value={cat}>
                              {cat}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="year">Year</Label>
                      <Input
                        id="year"
                        type="number"
                        value={formData.year}
                        onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                        min="2020"
                        max="2030"
                        data-testid="goal-year-input"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3 className="font-medium mb-3">Target & Progress</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="target_value">Target Value</Label>
                    <Input
                      id="target_value"
                      type="number"
                      value={formData.target_value}
                      onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
                      min="1"
                      data-testid="goal-target-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="current_value">Current Value</Label>
                    <Input
                      id="current_value"
                      type="number"
                      value={formData.current_value}
                      onChange={(e) => setFormData({ ...formData, current_value: e.target.value })}
                      min="0"
                      data-testid="goal-current-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="unit">Unit</Label>
                    <Input
                      id="unit"
                      value={formData.unit}
                      onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                      placeholder="e.g., %, books, hours"
                      data-testid="goal-unit-input"
                    />
                  </div>
                </div>
              </div>

              {isEditing && (
                <div className="form-section">
                  <h3 className="font-medium mb-3">Status</h3>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="goal-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((status) => (
                        <SelectItem key={status.value} value={status.value}>
                          {status.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="form-section">
                <h3 className="font-medium mb-3">Milestones</h3>
                <div className="flex gap-2 mb-2">
                  <Input
                    value={milestoneInput}
                    onChange={(e) => setMilestoneInput(e.target.value)}
                    placeholder="Add a milestone"
                    onKeyPress={(e) => e.key === "Enter" && (e.preventDefault(), addMilestone())}
                    data-testid="milestone-input"
                  />
                  <Button type="button" variant="outline" onClick={addMilestone} data-testid="add-milestone-btn">
                    Add
                  </Button>
                </div>
                {formData.milestones.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {formData.milestones.map((milestone, index) => (
                      <Badge key={index} variant="secondary" className="px-3 py-1">
                        {milestone}
                        <button
                          type="button"
                          onClick={() => removeMilestone(index)}
                          className="ml-2 hover:text-red-600"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Additional notes..."
                  rows={2}
                  data-testid="goal-notes-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-goal-btn">
                {isEditing ? "Update Goal" : "Create Goal"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Goal</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{selectedGoal?.title}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-goal"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
